import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { findLast, noop, isEqual } from 'lodash';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { InputValidations, useInputValidations } from '../components/GenerateStep/Validation';
import { Brief, BriefSection, BusinessInfo, Seller } from '../types';
import { getBusinessInfo, updateCompanyName } from '../api';
import { BigQueryEvent, bigQueryEvent } from '../monitoring/big-query';
import { bqEvents } from '../monitoring/bqEvents';

import { COMPANY_NAME_SECTION_TITLE } from '../hooks/useBriefData';
import { briefResponseQueryKey, briefTemplatesQueryKey } from './queryClient';
import { STEPS_IDS } from './constants';
import { hasSectionChanged } from './util';

interface TriggerDrawerExitParams {
  currentStepId: STEPS_IDS;
  type: 'back' | 'exit' | null;
}

export interface BriefContextValues {
  seller: Seller | null;
  subCategoryId: number | null;
  categoryId: number | null;
  userInput: string;
  setUserInput: (userInput: string) => void;
  isBriefInEditMode: boolean;
  editedSectionId: string | null;
  setEditedSectionId: (sectionId: string | null) => void;
  editedBrief: Brief | null;
  setEditedBrief: (brief: BriefContextValues['editedBrief']) => void;
  updateEditedBrief: (
    updateBrief: Partial<Brief> | null,
    updatedSectionId?: string,
    updateSectionValues?: Partial<BriefSection>
  ) => void;
  lastIncludedSectionId: string;
  setLastIncludedSectionId: (lastIncludedSectionId: string) => void;
  inputValidations: InputValidations;
  companyInfo?: BusinessInfo | null;
  source: string;
  componentName: string;
  sendBigQueryEvent: (event: BigQueryEvent) => void;
  onCancelModalSubmit: (step: STEPS_IDS, navigateById: (id: STEPS_IDS) => void) => void;
  isCancelModalOpenWithType: null | 'back' | 'exit';
  triggerDrawerExitOrBack: ({ type, currentStepId }: TriggerDrawerExitParams) => void;
  hasBriefTemplateChanged: boolean;
  onContactSellerClick?: () => void;
  shouldShowContactSellerButton: boolean;
  isBriefDrawerOpen: boolean;
  isBriefDrawerDisplayed: boolean;
  setBriefDrawerDisplayed: (value: boolean) => void;
  initialText?: string;
  allowSaveAfterEditOnly?: boolean;
  registerSectionRef: (sectionId: string, ref: HTMLElement | null) => void;
  scrollToSection: (sectionId: string) => void;
}

const BriefContext = createContext<BriefContextValues>({
  subCategoryId: null,
  categoryId: null,
  userInput: '',
  setUserInput: noop,
  isBriefInEditMode: false,
  editedSectionId: null,
  setEditedSectionId: noop,
  editedBrief: null,
  setEditedBrief: noop,
  updateEditedBrief: noop,
  lastIncludedSectionId: '',
  setLastIncludedSectionId: noop,
  inputValidations: {
    addServerErrorValidation: noop,
    isDebounceInputValid: false,
    failedValidation: undefined,
  },
  source: '',
  componentName: '',
  seller: null,
  sendBigQueryEvent: noop,
  onCancelModalSubmit: noop,
  isCancelModalOpenWithType: null,
  triggerDrawerExitOrBack: noop,
  hasBriefTemplateChanged: false,
  shouldShowContactSellerButton: true,
  isBriefDrawerOpen: false,
  isBriefDrawerDisplayed: false,
  setBriefDrawerDisplayed: noop,
  companyInfo: null,
  onContactSellerClick: undefined,
  initialText: '',
  allowSaveAfterEditOnly: false,
  registerSectionRef: noop,
  scrollToSection: noop,
});

export const useBriefContext = () => useContext(BriefContext);

export interface AppProviderProps {
  seller: BriefContextValues['seller'];
  source: BriefContextValues['source'];
  componentName: BriefContextValues['componentName'];
  onClose: () => void;
  categoryId: BriefContextValues['categoryId'];
  subCategoryId: BriefContextValues['subCategoryId'];
  children: React.ReactNode;
  onContactSellerClick?: BriefContextValues['onContactSellerClick'];
  isBriefDrawerOpen: BriefContextValues['isBriefDrawerOpen'];
  brief?: BriefContextValues['editedBrief'];
  onBriefChanged?: (brief: BriefContextValues['editedBrief']) => void;
  initialText?: BriefContextValues['initialText'];
  allowSaveAfterEditOnly?: BriefContextValues['allowSaveAfterEditOnly'];
}

export const AppProvider = ({
  seller,
  source,
  onClose,
  componentName,
  subCategoryId,
  categoryId,
  onContactSellerClick,
  children,
  isBriefDrawerOpen,
  brief,
  onBriefChanged = noop,
  initialText = '',
  allowSaveAfterEditOnly,
}: AppProviderProps) => {
  const [isCancelModalOpenWithType, setCancelModalOpenWithType] = useState<'back' | 'exit' | null>(null);
  const [userInput, setUserInput] = useState(initialText);
  const [editedSectionId, setEditedSectionId] = useState<string | null>(null);
  const isBriefInEditMode = editedSectionId !== null;
  const [editedBrief, setBrief] = useState<BriefContextValues['editedBrief']>(brief ?? null);
  const [lastIncludedSectionId, setLastIncludedSectionId] = useState('');
  const [isBriefDrawerDisplayed, setBriefDrawerDisplayed] = useState(isBriefDrawerOpen);

  const sectionRefsRef = useRef<Map<string, HTMLElement>>(new Map());

  const registerSectionRef = useCallback((sectionId: string, ref: HTMLElement | null) => {
    if (ref) {
      sectionRefsRef.current.set(sectionId, ref);
    } else {
      sectionRefsRef.current.delete(sectionId);
    }
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const sectionElement = sectionRefsRef.current.get(sectionId);
    if (sectionElement) {
      sectionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, []);

  useEffect(() => {
    setUserInput(initialText);
  }, [initialText]);

  useEffect(() => {
    setBriefDrawerDisplayed(isBriefDrawerOpen);
  }, [isBriefDrawerOpen]);

  useEffect(() => {
    if (!isEqual(brief, editedBrief)) {
      setBrief(brief ?? null);
    }
  }, [brief, editedBrief]);

  useEffect(() => {
    if (editedBrief?.sections?.length) {
      const focusedSection = findLast<BriefSection>(editedBrief.sections, (section: BriefSection) => !!section.focused);

      if (focusedSection && editedSectionId !== focusedSection.id) {
        setEditedSectionId(focusedSection.id);
      }
    }
  }, [editedBrief?.sections, editedSectionId]);

  const setEditedBrief = useCallback(
    (brief: BriefContextValues['editedBrief']) => {
      if (brief !== editedBrief) {
        setBrief(brief);
      }

      onBriefChanged(brief);
    },
    [editedBrief, onBriefChanged, setBrief]
  );

  const { data: companyInfo } = useQuery({
    queryKey: ['company_data'],
    queryFn: getBusinessInfo,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const queryCache = useQueryClient();
  const companyNameMutation = useMutation({
    mutationFn: updateCompanyName,
    onSuccess: () => {
      queryCache.invalidateQueries(['company_data']);
    },
  });

  const shouldShowContactSellerButton = !!onContactSellerClick;

  const isNewBrief = useMemo(() => !editedBrief?.id, [editedBrief]);

  const sendBigQueryEvent = useMemo(
    () =>
      bigQueryEvent({
        sellerId: seller?.id,
        componentName,
        source,
        categoryId,
        subCategoryId,
        isBriefInEditMode,
      }),
    [seller, source, categoryId, subCategoryId, isBriefInEditMode, componentName]
  );

  const hasBriefTemplateChanged = useMemo(() => {
    const briefResponse = queryCache.getQueryData<Brief>(briefResponseQueryKey);
    return (
      isNewBrief ||
      !!editedBrief?.sections.some((section, sectionIndex) => {
        const originalSection = briefResponse?.sections[sectionIndex] as BriefSection;

        return hasSectionChanged(originalSection, section);
      })
    );
  }, [editedBrief, isNewBrief, queryCache]);

  const closeHandler = ({ currentStepId }: { currentStepId: STEPS_IDS }) => {
    sendBigQueryEvent(bqEvents.professional_brief_exit({ currentStepId }));
    setUserInput(initialText);
    queryCache.invalidateQueries([briefTemplatesQueryKey]);
    setEditedBrief(null);
    queryCache.removeQueries(briefResponseQueryKey);
    onClose();
  };

  const onCancelModalSubmit = (step: STEPS_IDS, navigateById: (id: STEPS_IDS) => void) => {
    if (isCancelModalOpenWithType === 'back') {
      const prevScreen: STEPS_IDS = editedBrief?.id ? STEPS_IDS.home : STEPS_IDS.generate;
      setEditedBrief(null);
      queryCache.removeQueries(briefResponseQueryKey);
      navigateById(prevScreen);
    } else if (isCancelModalOpenWithType === 'exit') {
      closeHandler({ currentStepId: step });
    }
    setCancelModalOpenWithType(null);
  };

  const triggerDrawerExitOrBack = ({ type, currentStepId }: TriggerDrawerExitParams) => {
    const { home, done, generationError } = STEPS_IDS;
    if (type === 'exit') {
      if (
        [home, done, generationError].includes(currentStepId) ||
        (currentStepId === STEPS_IDS.generate && !userInput?.trim()) ||
        (currentStepId === STEPS_IDS.editBrief && !hasBriefTemplateChanged)
      ) {
        closeHandler({ currentStepId });
        return;
      }
    }
    setCancelModalOpenWithType(type);
  };

  const updateEditedBrief = (
    updateBrief: Partial<Brief> | null,
    updatedSectionId?: string,
    updateSectionValues?: Partial<BriefSection>
  ) => {
    const brief: Brief = {
      ...(editedBrief as Brief),
      ...updateBrief,
    };

    if (updatedSectionId && updateSectionValues) {
      brief.sections = brief.sections.map((oldSection) =>
        getUpdatedSection(oldSection, updatedSectionId, updateSectionValues)
      );
    }

    if (updateSectionValues?.title === COMPANY_NAME_SECTION_TITLE && companyInfo && updateSectionValues?.content) {
      companyNameMutation.mutate(updateSectionValues.content);
    }

    setEditedBrief(brief);
  };

  const getUpdatedSection = (
    oldSection: BriefSection,
    updatedSectionId: string,
    updateSectionValues: Partial<BriefSection>
  ) => {
    if (oldSection.id === updatedSectionId) {
      return { ...oldSection, ...updateSectionValues };
    }
    return oldSection;
  };

  return (
    <BriefContext.Provider
      value={{
        seller,
        source,
        componentName,
        subCategoryId,
        userInput,
        setUserInput,
        categoryId,
        isBriefInEditMode,
        editedSectionId,
        setEditedSectionId,
        editedBrief,
        setEditedBrief,
        updateEditedBrief,
        lastIncludedSectionId,
        setLastIncludedSectionId,
        inputValidations: useInputValidations(userInput),
        companyInfo,
        sendBigQueryEvent,
        onCancelModalSubmit,
        isCancelModalOpenWithType,
        triggerDrawerExitOrBack,
        hasBriefTemplateChanged,
        onContactSellerClick,
        shouldShowContactSellerButton,
        isBriefDrawerOpen,
        isBriefDrawerDisplayed,
        setBriefDrawerDisplayed,
        initialText,
        allowSaveAfterEditOnly,
        registerSectionRef,
        scrollToSection,
      }}
    >
      {children}
    </BriefContext.Provider>
  );
};
